'use client';

import { useEffect, useRef, useState } from 'react';
import { Check, Globe } from 'lucide-react';

export type DomainOption = { label: string; value: string | null };

/**
 * `null` is offered first and is the initial value, because the API's default
 * is a random spread across the pool rather than a fixed domain — that spread
 * is what stops one domain absorbing the whole pool's reputation risk.
 */
export function domainOptions(domains: string[]): DomainOption[] {
  return [
    { label: 'Any domain', value: null },
    ...domains.map((d) => ({ label: d, value: d })),
  ];
}

/**
 * Domain chooser for the hero form, sitting inline between the URL field and
 * the submit button — the slot two decorative, non-functional "option" icons
 * used to occupy. Keeping the control in that slot is what holds the form to a
 * single row on desktop.
 *
 * The trigger is the icon alone. Most visitors do not care which shared domain
 * they land on, so the control should cost the form as little width as it can
 * while still being there for the ones who do. A text label pushed the row out
 * of balance for a choice almost nobody makes. The current value lives in the
 * tooltip and the aria-label, and the icon turns brand-coloured once a specific
 * domain is picked, so a deliberate choice is still visible at a glance.
 *
 * The options open as an anchored popover rather than expanding in place: this
 * lives inside a flex row, so growing in place would break the row it exists to
 * preserve. On mobile the form stacks and this drops below the submit button
 * (`order-last`), keeping the URL field and the action as the first two things
 * on a narrow screen.
 */
export default function DomainPicker({
  domains,
  value,
  onChange,
}: {
  domains: string[];
  value: string | null;
  onChange: (domain: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // One domain is not a choice, and none means the pool never loaded.
  if (domains.length < 2) return null;

  const options = domainOptions(domains);

  return (
    <div
      ref={root}
      className="relative order-last flex items-center sm:order-none sm:border-l sm:border-slate-100 sm:px-1"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Choose a domain (currently ${value ?? 'Any domain'})`}
        title={`Domain: ${value ?? 'Any'}`}
        data-active={value !== null}
        className={`flex items-center justify-center rounded-lg p-2 transition-colors hover:bg-brand-50 hover:text-brand-600 ${
          value !== null || open ? 'text-brand-600' : 'text-slate-400'
        }`}
      >
        <Globe size={18} className="shrink-0" />
      </button>

      {open ? (
        <div
          role="listbox"
          className="absolute bottom-full right-0 z-20 mb-2 min-w-[10rem] rounded-xl border border-slate-200 bg-white p-1 shadow-xl sm:bottom-auto sm:top-full sm:mb-0 sm:mt-2"
        >
          {options.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.label}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left font-mono text-xs transition-colors ${
                  selected
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-brand-600'
                }`}
              >
                <Check
                  size={12}
                  className={`shrink-0 ${selected ? '' : 'invisible'}`}
                />
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
